/** @jsxImportSource @emotion/react */
import React, { useMemo } from 'react'
import { Typography } from '@mui/material'
import { useQuery } from '@apollo/client'
import { FIND_MCAT_STATISTICS_BY_GAME } from 'queries/gamesStats'
import {
  findMCATStatisticsByGame,
  findMCATStatisticsByGameVariables,
} from '__generatedTypes__/findMCATStatisticsByGame'
import { PageLoader } from 'components/PageLoader'
import Table from 'components/Table'
import * as classes from './styles'

type ItemTable1 = {
  id: string
  value: string
}

type ItemTable2 = {
  id: string
  count: string
  level: string
}

export type TotalStatisticsProps = {
  subjectId: string
  startDate?: string
  endDate?: string
}

export const TotalStatistics = ({ subjectId, startDate, endDate }: TotalStatisticsProps) => {
  const { data, loading } = useQuery<findMCATStatisticsByGame, findMCATStatisticsByGameVariables>(
    FIND_MCAT_STATISTICS_BY_GAME,
    {
      variables: {
        subjectId,
        startDate,
        endDate,
      },
    }
  )

  const { itemsTable1, itemsTable2 } = useMemo<{ itemsTable1: ItemTable1[]; itemsTable2: ItemTable2[] }>(() => {
    if (!data?.findMcatStatisticsByGame) {
      return {
        itemsTable1: [],
        itemsTable2: [],
      }
    }

    return {
      itemsTable1: [
        {
          id: 'Total Plays',
          value: String(data.findMcatStatisticsByGame.totalStatistics.totalPlays),
        },
        {
          id: 'Total Game Overs',
          value: String(data.findMcatStatisticsByGame.totalStatistics.totalGameOvers),
        },
        {
          id: 'Total Correct',
          value: String(data.findMcatStatisticsByGame.totalStatistics.totalCorrect),
        },
        {
          id: 'Total Incorrect',
          value: String(data.findMcatStatisticsByGame.totalStatistics.totalIncorrect),
        },
        {
          id: 'Total Time Spent',
          value: String(data.findMcatStatisticsByGame.totalStatistics.totalTimeSpent),
        },
        {
          id: 'Total Accumulated XP',
          value: String(data.findMcatStatisticsByGame.totalStatistics.totalAccumulatedXP),
        },
        {
          id: 'Average Attempt by each student (excluding new accounts + 1 attempts)',
          value: String(data.findMcatStatisticsByGame.totalStatistics.avgAttemptsByUser),
        },
        {
          id: 'Number of students that have reached the max level',
          value: String(data.findMcatStatisticsByGame.totalStatistics.userCountWithMaxLevel),
        },
      ],
      itemsTable2: [
        {
          id: 'Users that play more than 1 time',
          count: String(data.findMcatStatisticsByGame.playSnippets.statsByMoreThanOneAttempt.userCount),
          level: String(data.findMcatStatisticsByGame.playSnippets.statsByMoreThanOneAttempt.mostCommonLevel),
        },
        {
          id: 'Users that play more than 5 times',
          count: String(data.findMcatStatisticsByGame.playSnippets.statsByMoreThanFiveAttempts.userCount),
          level: String(data.findMcatStatisticsByGame.playSnippets.statsByMoreThanFiveAttempts.mostCommonLevel),
        },
      ],
    }
  }, [data?.findMcatStatisticsByGame])

  return (
    <div css={classes.wrapper}>
      <Typography variant="title4" css={classes.title}>
        Total Statistics
      </Typography>
      {loading ? (
        <PageLoader />
      ) : (
        <Table<ItemTable1>
          css={classes.tableWrapper}
          columns={[
            {
              title: 'Type',
              renderCell: ({ id }) => id,
              flex: 2,
            },
            {
              title: 'Users Count',
              renderCell: ({ value }) => value,
            },
          ]}
          items={itemsTable1}
          wrapperMinWidth={550}
        />
      )}

      <div css={classes.divider} />

      <Typography variant="title4" css={classes.title}>
        Play Snippets
      </Typography>
      {loading ? (
        <PageLoader />
      ) : (
        <Table<ItemTable2>
          css={classes.tableWrapper}
          columns={[
            {
              title: 'Type',
              renderCell: ({ id }) => id,
            },
            {
              title: 'Users Count',
              renderCell: ({ count }) => count,
            },
            {
              title: 'Most Common Level',
              renderCell: ({ level }) => level,
            },
          ]}
          items={itemsTable2}
          wrapperMinWidth={550}
        />
      )}
      <div css={classes.divider} />
    </div>
  )
}
